import type { IReCaptchaComposition } from 'vue-recaptcha-v3';
import { logger } from '@/utils/logger';

// useReCaptcha() is inject-based and therefore setup-only; this module-scope
// registration lets non-component code (Pinia stores, services) obtain tokens.
// Registered once from App.vue. Stays null in the Office add-in host, where
// the plugin is not installed.
let composition: IReCaptchaComposition | null = null;

export function registerRecaptcha(instance: IReCaptchaComposition | undefined): void {
  composition = instance ?? null;
}

export function isRecaptchaAvailable(): boolean {
  return composition !== null;
}

/**
 * Executes a reCAPTCHA Enterprise challenge for the given action.
 *
 * Returns null when the challenge cannot run (plugin not installed, script
 * blocked by an adblocker, offline). Callers pass the result on regardless —
 * the backend bypasses the gate in dev environments and answers
 * 403 `recaptcha_failed` otherwise.
 * @param action - The action name registered in the backend allow-list
 * @returns The challenge token, or null when unavailable
 */
export async function getRecaptchaToken(action: string): Promise<string | null> {
  if (!composition) return null;
  try {
    await composition.recaptchaLoaded();
    return await composition.executeRecaptcha(action);
  } catch (error: unknown) {
    logger.error('reCAPTCHA unavailable:', error);
    return null;
  }
}

/**
 * The `X-Recaptcha-Token` header for a gated endpoint call. A null value is
 * omitted from the request by the API client's header merge.
 * @param action - The action name registered in the backend allow-list
 * @returns A headers object for the generated client's `headers` option
 */
export async function recaptchaHeaders(
  action: string
): Promise<{ 'X-Recaptcha-Token': string | null }> {
  return { 'X-Recaptcha-Token': await getRecaptchaToken(action) };
}
